'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import FilterSection from '@/components/FilterSection';

const MEMBERS = [
  { slug: '', color: 'bg-[#D4AF37]', textColor: 'text-[#0A0A0A]' },
  { slug: 'GD', color: 'bg-[#ED609F]/20', textColor: 'text-[#ED609F]', activeColor: 'bg-[#ED609F]', activeTextColor: 'text-white' },
  { slug: 'Taeyang', color: 'bg-[#D7171E]/20', textColor: 'text-[#D7171E]', activeColor: 'bg-[#D7171E]', activeTextColor: 'text-white' },
  { slug: 'TOP', color: 'bg-[#8FC126]/20', textColor: 'text-[#8FC126]', activeColor: 'bg-[#8FC126]', activeTextColor: 'text-[#0A0A0A]' },
  { slug: 'Daesung', color: 'bg-[#F4E727]/20', textColor: 'text-[#F4E727]', activeColor: 'bg-[#F4E727]', activeTextColor: 'text-[#0A0A0A]' },
  { slug: 'Seungri', color: 'bg-[#1E92C6]/20', textColor: 'text-[#1E92C6]', activeColor: 'bg-[#1E92C6]', activeTextColor: 'text-white' },
];

const PLATFORMS = ['', 'ig-post', 'ig-story', 'twitter', 'weibo', 'other'];

const YEARS = ['', '2022', '2021', '2020', '2019', '2018', '2017', '2016', '2015', '2014'];

interface ArchiveFiltersProps {
  memberFilters: string[];
  yearFilters: string[];
  platformFilters: string[];
  searchQuery: string;
}

export default function ArchiveFilters({
  memberFilters,
  yearFilters,
  platformFilters,
  searchQuery,
}: ArchiveFiltersProps) {
  const t = useTranslations();
  const tFilters = useTranslations('filters');
  const tMembers = useTranslations('members');
  const tPlatforms = useTranslations('platforms');

  const platformNameMap: Record<string, string> = {
    '': tPlatforms('all'),
    'ig-post': tPlatforms('igPost'),
    'ig-story': tPlatforms('igStory'),
    'twitter': tPlatforms('twitter'),
    'weibo': tPlatforms('weibo'),
    'other': tPlatforms('other'),
  };

  function buildUrl(options: {
    member?: string;
    platform?: string;
    year?: string;
    toggleMember?: boolean;
    toggleYear?: boolean;
    togglePlatform?: boolean;
    clearYears?: boolean;
  }) {
    const p = new URLSearchParams();

    let newMembers = [...memberFilters];
    if (options.member !== undefined) {
      if (options.member === '') {
        newMembers = [];
      } else if (options.toggleMember) {
        if (newMembers.includes(options.member)) {
          newMembers = newMembers.filter(m => m !== options.member);
        } else {
          newMembers.push(options.member);
        }
      } else {
        newMembers = [options.member];
      }
    }

    let newYears = [...yearFilters];
    if (options.clearYears) {
      newYears = [];
    } else if (options.year !== undefined && options.toggleYear) {
      if (newYears.includes(options.year)) {
        newYears = newYears.filter(y => y !== options.year);
      } else {
        newYears.push(options.year);
      }
    }

    let newPlatforms = [...platformFilters];
    if (options.platform !== undefined) {
      if (options.platform === '') {
        newPlatforms = [];
      } else if (options.togglePlatform) {
        if (newPlatforms.includes(options.platform)) {
          newPlatforms = newPlatforms.filter(pl => pl !== options.platform);
        } else {
          newPlatforms.push(options.platform);
        }
      } else {
        newPlatforms = [options.platform];
      }
    }

    if (newMembers.length > 0) p.set('member', newMembers.join(','));
    if (newYears.length > 0) p.set('year', newYears.join(','));
    if (newPlatforms.length > 0) p.set('platform', newPlatforms.join(','));
    if (searchQuery) p.set('q', searchQuery);

    const qs = p.toString();
    return `/archive${qs ? `?${qs}` : ''}`;
  }

  const activeFilterCount = memberFilters.length + yearFilters.length + platformFilters.length;

  return (
    <FilterSection className="mb-6 md:mb-8" activeFilterCount={activeFilterCount}>
      <div className="space-y-3 md:space-y-4 pb-4">
        {/* Member Filter */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('member')}</span>
          <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
            {MEMBERS.map((member) => {
              const isActive = member.slug === ''
                ? memberFilters.length === 0
                : memberFilters.includes(member.slug);
              const isAll = member.slug === '';
              const name = member.slug === '' ? tMembers('all') : tMembers(member.slug);
              return (
                <Link
                  key={member.slug}
                  href={buildUrl({ member: member.slug, toggleMember: true })}
                  className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all flex-shrink-0 ${
                    isActive
                      ? isAll
                        ? 'bg-[#D4AF37] text-[#0A0A0A]'
                        : `${member.activeColor} ${member.activeTextColor}`
                      : isAll
                        ? 'bg-[#1A1A1A] text-[#888888] hover:text-[#F5F5F0] border border-white/10'
                        : `${member.color} ${member.textColor} border border-current/30`
                  }`}
                >
                  {name}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Year Filter */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('year')}</span>
          <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
            <Link
              href={buildUrl({ year: '', clearYears: true })}
              className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all flex-shrink-0 ${
                yearFilters.length === 0
                  ? 'bg-[#D4AF37] text-[#0A0A0A]'
                  : 'bg-[#1A1A1A] text-[#888888] hover:text-[#F5F5F0] border border-white/10'
              }`}
            >
              {t('common.all')}
            </Link>
            {YEARS.filter(y => y !== '').map((year) => {
              const isActive = yearFilters.includes(year);
              return (
                <Link
                  key={year}
                  href={buildUrl({ year, toggleYear: true })}
                  className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all flex-shrink-0 ${
                    isActive
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-[#1A1A1A] text-[#888888] hover:text-[#F5F5F0] border border-white/10'
                  }`}
                >
                  {year}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Platform Filter */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-[10px] md:text-xs text-[#666666] flex-shrink-0 w-12">{tFilters('platform')}</span>
          <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide pb-1">
            {PLATFORMS.map((slug) => {
              const isActive = slug === ''
                ? platformFilters.length === 0
                : platformFilters.includes(slug);
              return (
                <Link
                  key={slug}
                  href={buildUrl({ platform: slug, togglePlatform: true })}
                  className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-medium transition-all flex-shrink-0 ${
                    isActive
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-[#1A1A1A] text-[#888888] hover:text-[#F5F5F0] border border-white/10'
                  }`}
                >
                  {platformNameMap[slug]}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </FilterSection>
  );
}
